import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnDestroy, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { interval, Subject, takeUntil } from 'rxjs';
import { CancelUploadDialogComponent } from '../../../account/dialogs/cancel-upload-dialog/cancel-upload-dialog.component';
import { FailedUploadsDialogComponent } from '../../../account/dialogs/failed-uploads-dialog/failed-uploads-dialog.component';
import { DialogService } from '../../../services/dialog.service';
import { ProgressToastService, ProgressToastUpload } from '../../../services/progress-toast.service';
import { UploadStatService } from '../../../services/upload-stat.service';
import { UploadService } from '../../../services/upload.service';
import { TimeRemainingPipe } from '../../../shared-extra/pipes/time-remaining.pipe';
import { UploadOrderPipe } from '../../../upload-info/pipes/upload-order.pipe';
import { UploadProgressPipe } from '../../../upload-info/pipes/upload-progress.pipe';
import { ButtonDirective } from '../../directives/button.directive';
import { TooltipDirective } from '../../directives/tooltip.directive';
import { DataUnitsBigIntPipe } from '../../pipes/data-units-big-int.pipe';
import { CircleProgressBarComponent } from '../circle-progress-bar/circle-progress-bar.component';
import { ProgressUploadToastsItemCompletedComponent } from '../progress-upload-toasts-item-completed/progress-upload-toasts-item-completed.component';
import { AnimatedSvgComponent } from '../svg/animated-svg.component';
import { SvgComponent } from '../svg/svg.component';

@Component({
  selector: 'app-progress-upload-toasts-item',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ProgressUploadToastsItemCompletedComponent,
    CircleProgressBarComponent,
    DataUnitsBigIntPipe,
    TimeRemainingPipe,
    ButtonDirective,
    SvgComponent,
    UploadOrderPipe,
    UploadProgressPipe,
    AnimatedSvgComponent,
    TooltipDirective,
  ],
  templateUrl: './progress-upload-toasts-item.component.html',
  styleUrl: './progress-upload-toasts-item.component.scss',
})
export class ProgressUploadToastsItemComponent implements OnDestroy {
  private uploadStatService = inject(UploadStatService);
  private progressToastService = inject(ProgressToastService);
  private dialog = inject(DialogService);
  private uploadService = inject(UploadService);
  toast = input.required<ProgressToastUpload>();
  standalone = input<boolean>();
  destroyed$ = new Subject<void>();

  interval$ = interval(1000).pipe(takeUntil(this.destroyed$));
  interval = toSignal(this.interval$);

  minimized = signal(this.progressToastService.toasts$.value.length > 1);
  allUploads = toSignal(this.uploadService.uploads$);
  uploads = computed(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const timer = this.interval(); // just here to trigger the update
    const galleryId = this.toast().gallery.id;
    return this.allUploads().filter(upload => upload.galleryId === galleryId);
  });
  totalSize = computed(() => this.uploadStatService.getTotalSize(this.uploads()));
  completed = signal(false);
  failedUploads = computed(() => this.uploads().filter(upload => upload.status === 'error'));
  uploadedAssets = computed(() => this.uploads().filter(upload => upload.status === 'done'));
  uploadedTotalSize = computed(() => this.uploadedAssets().reduce((acc, upload) => acc + upload.file.size, 0));
  remainingAssets = computed(() => this.uploads().filter(upload => !['done', 'error'].includes(upload.status)));
  uploadProgress = computed(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const timer = this.interval(); // just here to trigger the update
    const size = this.totalSize();
    const remainingSize = this.uploadStatService.getRemainingSize(this.uploads(), this.uploadSpeed());
    const remainingPercent = (Number(remainingSize) / Number(size)) * 100;
    return 100 - remainingPercent;
  });
  uploadSpeed = computed(() => this.uploadStatService.getUploadSpeed(this.uploads()));
  concurrency = computed(() => this.uploadStatService.getConcurrency(this.uploads()));
  remainingSize = computed(() => this.uploadStatService.getRemainingSize(this.uploads(), this.uploadSpeed()));

  lastRemainingTime = { value: 0, time: 0 };
  remainingTime = computed(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const timer = this.interval(); // just here to trigger the update
    const remaining = this.uploadStatService.getRemainingTime(this.uploads(), this.uploadSpeed());
    if (Date.now() - this.lastRemainingTime.time > 1000) {
      this.lastRemainingTime.time = Date.now();
      this.lastRemainingTime.value = remaining;
      return remaining;
    }
    return this.lastRemainingTime.value;
  });

  constructor() {
    effect(() => {
      const uploads = this.uploads();
      const completed = this.completed();
      if (uploads.every(upload => ['done', 'error'].includes(upload.status))) {
        if (!completed) {
          if (uploads.length) {
            setTimeout(() => this.completed.set(true), 600);
          } else {
            if (!this.standalone()) {
              this.progressToastService.removeToast(this.toast().id);
            }
          }
        }
      } else {
        this.completed.set(false);
      }
    });
  }

  public async showCancelUploadDialog() {
    const gallery = this.toast().gallery;
    const resp = await this.dialog.open(CancelUploadDialogComponent);
    if (resp) {
      this.uploadService.cancelUploads(gallery.id);
      this.close();
    }
  }

  close() {
    if (this.standalone()) return;
    this.progressToastService.removeToast(this.toast().id);
    this.uploadService.removeUploads(this.toast().gallery.id);
  }

  showFailedUploads() {
    this.dialog.open(FailedUploadsDialogComponent, {
      failedUploads: this.failedUploads(),
      gallery: this.toast().gallery,
    });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
